// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
// import Clickable from "@insite/mobius/Clickable"; // Starter Custom Code
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

/* Starter Custom Code - Start */
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Carousel, { CarouselPresentationProps } from "@store/Overrides/Components/Carousel";
import { imageStyles } from "@store/Themes/common/mixins/image";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { BreakpointValueProp } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles, spacingStyles } from "@toolkit/Themes/common/mixins";

const imageWidth = pxToRem(70);
const imageSpacing = pxToRem(12);
const defaultNumberOfVisibleThumbnails = [3, 4, 4, 5];

export const productImageCarouselImageWidth = imageWidth;
/* Starter Custom Code - End */

interface OwnProps {
    scrollingBehavior?: string;
    numberOfVisibleThumbnails?: BreakpointValueProp<number>; // Starter Custom Code
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    /** @deprecated Use CarouselSlideContainer object instead. */
    carouselSlidesContainer?: InjectableCss;
    /** @deprecated Use CarouselSlide object instead. */
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    verticalPrevArrowGridItem?: GridItemProps;
    verticalCarouselGridItem?: GridItemProps;
    verticalNextArrowGridItem?: GridItemProps;
    // Starter Custom Code
    mainWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    carousel?: CarouselPresentationProps;
    clickable?: ClickablePresentationProps;
}

/* Starter Custom Code - Start */
const getImageCss = (borderColor: string) => css`
    ${x => x.theme.lazyImage?.defaultProps?.css}
    border: 2px solid ${getColor(borderColor)};
    cursor: pointer;
    z-index: -1; /* Fix outline appearance on focus */
`;
/* Starter Custom Code - End */

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    mainWrapper: {
        css: css`
            ${spacingStyles("1em", "1em")}
            height: 100%;
            width: 100%;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    carousel: {
        arrowsGap: imageSpacing,
        arrowsPositionVariant: "inside",
        itemsGap: imageSpacing,
        itemsSize: imageWidth,
        itemsToScroll: 1,
        extendedStyles: {
            carouselContainer: {
                css: css`
                    ${breakpoint(350, "down")} {
                        .axis-x && {
                            margin: ${pxToRem(0, -4)};
                        }

                        .axis-y && {
                            margin: ${pxToRem(-4, 0)};
                        }
                    }
                `,
            },
        },
    },
    clickable: {
        css: css`
            ${focusStyles(true)}
            ${x => imageStyles(x.theme.design.product.image.small.ratio)}
            z-index: 1;
            width: ${imageWidth};
        `,
    },
    image: {
        css: getImageCss("transparent"),
    },
    selectedImage: {
        css: getImageCss("primary"),
    },
};

export const CarouselSlidesContainer = styled.div<
    InjectableCss & { isVertical: boolean; numberOfVisibleThumbnails: number }
>`
    display: flex;
    flex-direction: ${props => (props.isVertical ? "column" : "row")};
    align-items: ${props => (props.isVertical ? "center" : "initial")};
    ${props => (props.isVertical ? `height: ${props.numberOfVisibleThumbnails * 74}px;` : "")}
    ${injectCss};
`;

export const CarouselSlide = styled.div<InjectableCss & { numberOfVisibleThumbnails: number }>`
    flex: 0 0 ${props => 100 / props.numberOfVisibleThumbnails}%;
    padding: 0;
    position: relative;
    ${injectCss};
`;

const ProductImageCarousel = ({
    productSettings,
    productContext,
    scrollingBehavior,
    numberOfVisibleThumbnails = defaultNumberOfVisibleThumbnails, // Starter Custom Code
    selectedIndex,
    onSelectImage,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;

    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };

    const isVertical = scrollingBehavior === "vertical";
    // Starter Removed - Embla implementation

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.indexOf("?") > 0 ? "&thumb" : "?thumb");
        }
        return "";
    };

    if (!product || !product.images || product.images?.length === 0) {
        return null;
    }

    return (
        /* Starter Custom Code - Start */
        <StyledWrapper {...styles.mainWrapper}>
            <StyledWrapper {...styles.innerWrapper}>
                <StyledWrapper {...styles.carouselContainer}>
                    <Carousel
                        {...styles.carousel}
                        id={`product-image-carousel-${product.id}`}
                        itemsPerSlide={numberOfVisibleThumbnails}
                        axis={isVertical ? "y" : "x"}
                        slides={product.images.map((productImage, index) => ({
                            id: productImage.id,
                            renderComponent: () => {
                                return (
                                    <Clickable
                                        onClick={() => imageClickHandler(index)}
                                        {...styles.clickable}
                                        id={`product_${product.id}`}
                                        data-test-key={product.id}
                                        data-test-selector="carousel_slide"
                                        key={product.id}
                                    >
                                        <LazyImage
                                            {...(index === selectedImageIndex ? styles.selectedImage : styles.image)}
                                            src={getProductImageThumbPath(productImage)}
                                            altText={productImage.imageAltText}
                                        />
                                    </Clickable>
                                );
                            },
                        }))}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
        /* Starter Custom Code - End */
    );
};

export default connect(mapStateToProps)(withProductContext(ProductImageCarousel));
